import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/**
 * Dark-premium back button — clear, visible, with red accent.
 * - Uses navigate(-1) if this page was reached from within the SPA,
 *   otherwise falls back to `fallback` prop.
 * - If `hideIfRoot` is true, renders nothing when there is no prior history
 *   (useful for pages that are themselves the "home" of a section).
 */
export default function BackButton({
  fallback = "/app",
  label = "Back",
  className = "",
  hideIfRoot = false,
  compact = false,
}) {
  const nav = useNavigate();
  const loc = useLocation();
  const hasPrev = loc.key !== "default" && window.history.length > 1;
  // Iter 39: also hide on the main home/feed routes so the back button can't
  // accidentally appear there via any caller
  const rootRoutes = ["/app", "/app/", "/", "/feed", "/app/feed"];
  const isRoot = rootRoutes.includes(loc.pathname);

  if (isRoot) return null;
  if (hideIfRoot && !hasPrev) return null;

  const goBack = () => {
    if (hasPrev) nav(-1);
    else nav(fallback, { replace: true });
  };

  const sizing = compact
    ? "h-9 pl-2 pr-3 text-sm"
    : "h-10 pl-2.5 pr-4 text-sm";

  return (
    <button
      data-testid="back-btn"
      onClick={goBack}
      aria-label="Go back"
      className={`inline-flex items-center gap-1.5 ${sizing} rounded-full border-2 border-[#DC2626]/40 bg-black/70 backdrop-blur-md hover:border-[#DC2626] hover:bg-[#DC2626]/15 text-white font-medium shadow-lg shadow-black/40 transition-all active:scale-95 ${className}`}
    >
      <ArrowLeft size={16} strokeWidth={2.6} className="text-[#DC2626]"/>
      {label && <span>{label}</span>}
    </button>
  );
}
